"use client";

import { useRef, useState, type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { DocumentCard } from "@huub-web/shared";
import type { Timeout } from "@huub-web/ui/types/Timeout";
import { useMaybePubSub } from "@huub-web/ui/hooks/usePubSub";

import { useTemplates } from "../../hooks/useTemplates";

/**
 * Props for DocumentDownloadTemplate
 */
export interface DocumentDownloadTemplateProps {
  signEndpoint?: string;
  downloadEndpoint?: string;
  viewEndpoint?: string;
  signed?: boolean;
  variant?: "default" | "compact";
  title?: string;
  description?: string;
  value?: string;
  documentTitle?: string;
  documentSubtitle?: string;
  acknowledgementLabel?: string;
  readLabel?: string;
}

/**
 * DocumentDownload template component
 * @deprecated Use DocumentViewerTemplate instead
 *
 * @param props - DocumentDownloadTemplate props
 * @returns JSX.Element
 */
export const DocumentDownloadTemplate: FC<
  DocumentDownloadTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  signEndpoint,
  downloadEndpoint,
  viewEndpoint,
  signed: signedProp,
  variant = "compact",
  title,
  description,
  value,
  documentTitle,
  documentSubtitle,
  acknowledgementLabel,
  readLabel,
  ref,
}) => {
  const { t } = useTranslation();
  const { lockUI, $post, downloadDocument } = useTemplates();
  const pubSub = useMaybePubSub();

  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<Timeout | null>(null);
  const [isSigned, setIsSigned] = useState(signedProp);

  const handleSign = () => {
    if (!signEndpoint) {
      console.error("[OTDTemplate] No sign endpoint provided");
      return;
    }

    setIsLoading(true);

    timeoutRef.current = setTimeout(() => {
      $post(signEndpoint, {})
        .then(() => {
          setIsSigned(true);
        })
        .catch(() => {
          pubSub?.publish("snackbar", {
            body: t("templating.document.signError"),
            variant: "error",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  };

  const handleDownload = () => {
    if (!downloadEndpoint) {
      console.error("[OTDTemplate] No download endpoint provided");
      return;
    }
    downloadDocument(downloadEndpoint);
  };

  return (
    <DocumentCard
      ref={ref}
      disabled={lockUI}
      onSign={handleSign}
      onDownload={handleDownload}
      loading={isLoading}
      signed={isSigned}
      download={!!downloadEndpoint}
      signing={!!signEndpoint}
      url={viewEndpoint}
      variant={variant}
      title={title}
      description={description}
      value={value}
      documentTitle={documentTitle}
      documentSubtitle={documentSubtitle}
      acknowledgementLabel={acknowledgementLabel}
      readLabel={readLabel}
    />
  );
};
